import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../config/firebase';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, where, getDocs } from 'firebase/firestore';
import './BehindTheCage.css';

const unitLogo = '/images/LP_Unit_logo.png';

function BehindTheCage() {
  const { user, isAdmin, logout } = useAuth();
  const [requests, setRequests] = useState([]);
  const [armorers, setArmorers] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterArmorer, setFilterArmorer] = useState('all');
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Request types (includes old types for backwards compatibility)
  const requestTypes = [
    'New - Helmet',
    'New - Vest', 
    'New - Custom',
    'New - Headwear',
    'Revision - Helmet',
    'Revision - Vest',
    'ZAP',
    'Helmet',  // Legacy
    'Vest'     // Legacy
  ];

  // Update current time every minute for timer calculations
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch armorers (only armorer and admin roles)
  useEffect(() => {
    const fetchArmorers = async () => {
      // Fetch all approved users and filter for armorers/admins client-side
      // This handles both 'role' and 'displayRole' field variations
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const armorersList = usersSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(user => {
          const role = user.role || user.displayRole || '';
          return role === 'armorer' || role === 'admin';
        });
      setArmorers(armorersList);
    };
    fetchArmorers();
  }, []);

  // Subscribe to requests
  useEffect(() => {
    const requestsQuery = query(
      collection(db, 'requests'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(requestsQuery, (snapshot) => {
      const requestsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRequests(requestsList);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      const requestRef = doc(db, 'requests', requestId);
      const now = new Date();
      
      await updateDoc(requestRef, { 
        status: newStatus,
        statusChangedAt: now,
        [`statusHistory.${newStatus}`]: now
      });
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleAssignArmorer = async (requestId, armorerId) => {
    try {
      const armorer = armorers.find(a => a.id === armorerId);
      const requestRef = doc(db, 'requests', requestId);
      await updateDoc(requestRef, { 
        assignedTo: armorerId,
        assignedToName: armorer ? (armorer.username || armorer.displayName || armorer.email) : 'Unassigned'
      });
    } catch (error) {
      console.error('Error assigning armorer:', error);
    }
  };

  const filteredRequests = requests.filter(r => {
    // Status filter
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    // Type filter
    if (filterType !== 'all' && r.type !== filterType) return false;
    // Armorer filter - handle arrays, IDs, and name strings
    if (filterArmorer !== 'all') {
      const assignedTo = r.assignedTo;
      const selectedArmorer = armorers.find(a => a.id === filterArmorer);
      const selectedArmorerName = selectedArmorer?.username || selectedArmorer?.displayName || '';
      
      if (Array.isArray(assignedTo)) {
        // Multi-armorer: check if selected armorer ID is in the array
        if (!assignedTo.includes(filterArmorer)) return false;
      } else if (assignedTo) {
        // Single value: could be ID or name string
        if (assignedTo !== filterArmorer && assignedTo !== selectedArmorerName) return false;
      } else {
        // No assignment
        return false;
      }
    }
    return true;
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeElapsed = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = currentTime;
    const diffMs = now - date;
    
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const getTimeInStatus = (request) => {
    const statusChangedAt = request.statusChangedAt || request.createdAt;
    return getTimeElapsed(statusChangedAt);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Unassigned': return '#ff6b6b';
      case 'In Progress': return '#ffd93d';
      case 'Ready for Config': return '#6bcb77';
      case 'Pushed to ModPack': return '#4d96ff';
      case 'Payment Received': return '#9b59b6';
      case 'Closed Out': return '#888';
      default: return '#888';
    }
  };

  // Time in queue coloring: 0-3 days none, 4-6 days yellow, 6+ days red
  const getQueueUrgencyClass = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffDays = (currentTime - date) / (1000 * 60 * 60 * 24);
    
    if (diffDays >= 6) return 'urgency-high';
    if (diffDays >= 4) return 'urgency-medium';
    return '';
  };

  // Check if request should be grayed out (Closed Out status)
  const isClosedOut = (request) => {
    return request.status === 'Closed Out';
  };

  // Handle multi-armorer assignment
  const handleArmorerToggle = async (requestId, armorerId, currentAssignees = []) => {
    try {
      const requestRef = doc(db, 'requests', requestId);
      let newAssignees;
      
      if (currentAssignees.includes(armorerId)) {
        // Remove armorer
        newAssignees = currentAssignees.filter(id => id !== armorerId);
      } else {
        // Add armorer
        newAssignees = [...currentAssignees, armorerId];
      }
      
      // Get names for display
      const assigneeNames = newAssignees.map(id => {
        const armorer = armorers.find(a => a.id === id);
        return armorer ? (armorer.username || armorer.displayName || armorer.email) : 'Unknown';
      });
      
      await updateDoc(requestRef, { 
        assignedTo: newAssignees,
        assignedToNames: assigneeNames,
        assignedToName: assigneeNames.join(', ') || 'Unassigned'
      });
    } catch (error) {
      console.error('Error updating armorers:', error);
    }
  };

  return (
    <div className="cage-page" style={{ backgroundImage: `url(/images/CSQD-Vol-I.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
      <nav className="cage-navbar">
        <Link to="/" className="nav-left">
          <img src={unitLogo} alt="Logo" className="unit-logo" />
          <h1 className="site-title">Creature Supply</h1>
        </Link>
        <div className="nav-right">
          <Link to="/" className="nav-link">Home</Link>
          <Link to="/metrics" className="nav-link">Metrics</Link>
          {isAdmin && <Link to="/admin" className="nav-link admin-nav">Admin</Link>}
          <span className="user-badge armorer-badge">{isAdmin ? 'ADMIN' : 'ARMORER'}</span>
          <button className="logout-btn" onClick={logout}>Logout</button>
        </div>
      </nav>

      <main className="cage-content">
        <div className="page-title"><h2>View Requests</h2></div>

        <div className="stats-header">
          <div className="stat-card">
            <span className="stat-number">{requests.length}</span>
            <span className="stat-label">Total Requests</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{requests.filter(r => r.status === 'Unassigned').length}</span>
            <span className="stat-label">Unassigned</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{requests.filter(r => r.status === 'In Progress').length}</span>
            <span className="stat-label">In Progress</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{requests.filter(r => r.status === 'Pushed to ModPack' || r.status === 'Closed Out').length}</span>
            <span className="stat-label">Completed</span>
          </div>
        </div>

        <div className="filter-bar">
          <div className="filter-group">
            <label>Filter by Status:</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="filter-select">
              <option value="all">All Statuses</option>
              <option value="Unassigned">Unassigned</option>
              <option value="In Progress">In Progress</option>
              <option value="Ready for Config">Ready for Config</option>
              <option value="Pushed to ModPack">Pushed to ModPack</option>
              <option value="Payment Received">Payment Received</option>
              <option value="Closed Out">Closed Out</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Filter by Type:</label>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="filter-select">
              <option value="all">All Types</option>
              {requestTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>Filter by Armorer:</label>
            <select value={filterArmorer} onChange={(e) => setFilterArmorer(e.target.value)} className="filter-select">
              <option value="all">All Armorers</option>
              {armorers.map(armorer => (
                <option key={armorer.id} value={armorer.id}>
                  {armorer.username || armorer.displayName || armorer.email}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="requests-table-container">
          {loading ? (
            <div className="loading">Loading requests...</div>
          ) : filteredRequests.length === 0 ? (
            <div className="no-requests">No requests found</div>
          ) : (
            <table className="requests-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>User</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Time in Queue</th>
                  <th>Time in Status</th>
                  <th>Assigned To</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map(request => {
                  const assignedArmorers = Array.isArray(request.assignedTo) ? request.assignedTo : (request.assignedTo ? [request.assignedTo] : []);
                  return (
                  <tr key={request.id} className={`${getQueueUrgencyClass(request.createdAt)} ${isClosedOut(request) ? 'row-completed' : ''}`}>
                    <td>{formatDate(request.createdAt)}</td>
                    <td><span className="user-email">{request.username || request.userEmail}</span></td>
                    <td><span className={`type-badge ${request.type?.toLowerCase().replace(/\s+/g, '-')}`}>{request.type || 'N/A'}</span></td>
                    <td>
                      <select
                        value={request.status || 'Unassigned'}
                        onChange={(e) => handleStatusChange(request.id, e.target.value)}
                        className="status-select"
                        style={{ borderColor: getStatusColor(request.status) }}
                      >
                        <option value="Unassigned">Unassigned</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Ready for Config">Ready for Config</option>
                        <option value="Pushed to ModPack">Pushed to ModPack</option>
                        <option value="Payment Received">Payment Received</option>
                        <option value="Closed Out">Closed Out</option>
                      </select>
                    </td>
                    <td className={`timer-cell ${getQueueUrgencyClass(request.createdAt)}`}><span className="timer">{getTimeElapsed(request.createdAt)}</span></td>
                    <td className="timer-cell"><span className="timer status-timer">{getTimeInStatus(request)}</span></td>
                    <td>
                      <div className="multi-armorer-dropdown">
                        <div className="armorer-display">
                          {assignedArmorers.length === 0 ? 'Unassigned' : 
                            assignedArmorers.map(id => {
                              const a = armorers.find(arm => arm.id === id);
                              return a ? (a.username || a.email) : 'Unknown';
                            }).join(', ')
                          }
                        </div>
                        <div className="armorer-checkboxes">
                          {armorers.map(armorer => (
                            <label key={armorer.id} className="armorer-checkbox-label">
                              <input 
                                type="checkbox"
                                checked={assignedArmorers.includes(armorer.id)}
                                onChange={() => handleArmorerToggle(request.id, armorer.id, assignedArmorers)}
                              />
                              {armorer.username || armorer.displayName || armorer.email}
                            </label>
                          ))}
                        </div>
                      </div>
                    </td>
                    <td><button className="view-btn" onClick={() => setSelectedRequest(request)}>View Details</button></td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {selectedRequest && (
        <div className="modal-overlay" onClick={() => setSelectedRequest(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedRequest(null)}>×</button>
            <h2 className="modal-title">Request Details</h2>
            <div className="detail-grid">
              <div className="detail-item"><label>Submitted By:</label><span>{selectedRequest.username || selectedRequest.userEmail}</span></div>
              <div className="detail-item"><label>ZAP:</label><span>{selectedRequest.zap}</span></div>
              <div className="detail-item"><label>Discord:</label><span>{selectedRequest.discordName}</span></div>
              <div className="detail-item"><label>PayPal:</label><span>{selectedRequest.paypalEmail}</span></div>
              <div className="detail-item"><label>Date:</label><span>{formatDate(selectedRequest.createdAt)}</span></div>
              <div className="detail-item"><label>Type:</label><span>{selectedRequest.type}</span></div>
              <div className="detail-item"><label>Status:</label><span style={{ color: getStatusColor(selectedRequest.status) }}>{selectedRequest.status}</span></div>
              <div className="detail-item"><label>Time in Queue:</label><span>{getTimeElapsed(selectedRequest.createdAt)}</span></div>
            </div>
            <div className="detail-section">
              <h3>Configuration</h3>
              <div className="config-details">
                {selectedRequest.vestType && <p><strong>Vest:</strong> {selectedRequest.vestType}</p>}
                {selectedRequest.placard && <p><strong>Placard:</strong> {selectedRequest.placard}</p>}
                {selectedRequest.belt && <p><strong>Belt:</strong> {selectedRequest.belt}</p>}
                {selectedRequest.holster && <p><strong>Holster:</strong> {selectedRequest.holster}</p>}
                {selectedRequest.helmet && <p><strong>Helmet:</strong> {selectedRequest.helmet}</p>}
                {selectedRequest.rails && <p><strong>Rails:</strong> {selectedRequest.rails}</p>}
                {selectedRequest.cover && <p><strong>Cover:</strong> {selectedRequest.cover}</p>}
                {selectedRequest.batteryPack && <p><strong>Battery:</strong> {selectedRequest.batteryPack}</p>}
                {selectedRequest.strobe && <p><strong>Strobe:</strong> {selectedRequest.strobe}</p>}
                {selectedRequest.earProtection && <p><strong>Ear Pro:</strong> {selectedRequest.earProtection}</p>}
                {selectedRequest.taskLight && <p><strong>Task Light:</strong> {selectedRequest.taskLight}</p>}
                {selectedRequest.nodMount && <p><strong>NOD Mount:</strong> {selectedRequest.nodMount}</p>}
                {selectedRequest.nod && <p><strong>NOD:</strong> {selectedRequest.nod}</p>}
                {/* Revision fields */}
                {selectedRequest.helmetDetails && <p><strong>Helmet Revision:</strong> {selectedRequest.helmetDetails}</p>}
                {selectedRequest.vestDetails && <p><strong>Vest Revision:</strong> {selectedRequest.vestDetails}</p>}
                {selectedRequest.beltDetails && <p><strong>Belt Revision:</strong> {selectedRequest.beltDetails}</p>}
                {/* ZAP Change fields */}
                {selectedRequest.currentZap && <p><strong>Current ZAP:</strong> {selectedRequest.currentZap}</p>}
                {selectedRequest.updatedZap && <p><strong>Updated ZAP:</strong> {selectedRequest.updatedZap}</p>}
                {selectedRequest.colorOption && selectedRequest.type === 'ZAP' && <p><strong>Color Option:</strong> {selectedRequest.colorOption}</p>}
                {/* Custom items */}
                {selectedRequest.items && selectedRequest.items.length > 0 && (
                  <>
                    <p><strong>Items:</strong></p>
                    {selectedRequest.items.map((item, idx) => (
                      <p key={idx} style={{ marginLeft: '1rem' }}>
                        • {item.type}: {item.specification || item.description}
                        {item.imageLink && <span> (Ref: {item.imageLink})</span>}
                      </p>
                    ))}
                  </>
                )}
              </div>
            </div>
            {selectedRequest.comments && (
              <div className="detail-section">
                <h3>Comments</h3>
                <p className="user-notes">{selectedRequest.comments}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default BehindTheCage;
